// Receipts the assistant has read, waiting on a person.
//
// ─── WHY THIS SCREEN EXISTS ─────────────────────────────────────────────────
//
// Owner, 2026-08-22: "The AI reads it and PROPOSES. A human confirms before
// anything is written. Never let the AI write a payment amount on its own."
//
// Until that day the assistant read a bank slip with a vision model and, if the
// amount cleared the minimum, marked the order PAID by itself. It no longer
// can — `transitionToPaid` is not even imported on that path any more. What it
// files instead is a `payment_proposals` row, and this is the only place those
// rows become money.
//
// ⚠️ WITHOUT THIS SCREEN THE CHANGE MADE THINGS WORSE, NOT BETTER. Proposals
// would pile up unread, and a customer who HAD paid would sit on an order
// nobody knew was paid. Visibility is the whole point — which is why the count
// rides in the summary and the group opens itself when anything is waiting.
//
// ⚠️ AND ACCEPTING ONE DOES NOT CONFIRM THE ORDER. It records money received.
// « Confirmer la commande » is a separate human click, and the card says so on
// every row — the owner was explicit that the assistant "must never confirm
// itself", and a screen that quietly did it for him would be the same failure
// wearing a button.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi, type PaymentProposal } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';

/** What the model could not read. Shown as words, never as a zero — a blank
 *  amount displayed as "0 MAD" is a number somebody could accept by reflex. */
export function orUnreadable(v: string | number | null | undefined, t: (s: string) => string): string {
  const s = String(v ?? '').trim();
  return s ? s : t('illisible');
}

/**
 * Does the chosen type contradict the amount?
 *
 * ⚠️ STATED, NEVER AUTO-CORRECTED. 300 MAD filed as « paiement complet » on an
 * 821 MAD album is how the other 521 never arrives — but silently flipping the
 * choice would take the decision away from the person the whole screen exists
 * to put in charge. So it asks.
 *
 * Exported because it is the only real judgement on this card, and JSX is not
 * where a judgement about money should hide.
 */
export function depositMismatch(
  kind: 'advance' | 'full',
  amount: number | null | undefined,
  total: number | string | null | undefined,
): boolean {
  const a = Number(amount);
  const t = Number(total);
  if (!Number.isFinite(a) || a <= 0) return false;      // nothing entered yet
  if (!Number.isFinite(t) || t <= 0) return false;      // no total to compare
  // The same dirham of slack the worker uses, so the screen and the server
  // never disagree about what "covers the bill" means.
  return kind === 'full' ? a + 1 < t : a + 1 >= t;
}

/** May « Valider » be pressed? Money needs a positive number, always. */
export function canAccept(amount: string, busy: boolean): boolean {
  return !busy && Number(amount) > 0;
}

// ─── THE RECEIPT AGAINST THE ORDER'S PRICE (owner, 2026-09-29) ─────────────
//
// « compare the pricing that we have ». The same comparison as the WhatsApp
// alert and the manager's answer (worker lib/paymentProposal.ts `priceLine`),
// with the same dirham of slack. `web/` cannot import from `worker/`; if one
// changes, change both.

const SLACK = 1;
const two = (n: number) => String(Math.round(n * 100) / 100);

/** The type the price implies, or null with no price / no amount. */
export function kindFromPrice(
  amount: number | null | undefined,
  total: number | string | null | undefined,
): 'advance' | 'full' | null {
  const a = Number(amount);
  const t = Number(total);
  if (amount == null || !Number.isFinite(a) || a <= 0 || total == null || !Number.isFinite(t) || t <= 0) return null;
  return a + SLACK >= t ? 'full' : 'advance';
}

/** One line, the receipt against the price — or null when the order has none. */
export function priceComparisonLine(
  amount: number | null | undefined,
  total: number | string | null | undefined,
  t: (s: string, vars?: Record<string, string | number>) => string,
): string | null {
  const tot = Number(total);
  if (total == null || !Number.isFinite(tot) || tot <= 0) return null;
  const a = Number(amount);
  if (amount == null || !Number.isFinite(a) || a <= 0) return t('Prix de la commande : {total} MAD', { total: two(tot) });
  if (a + SLACK < tot) {
    return t('Prix de la commande : ⚠️ {amount} sur {total} MAD — il manque {short} MAD', { amount: two(a), total: two(tot), short: two(tot - a) });
  }
  if (a > tot + SLACK) {
    return t('Prix de la commande : {total} MAD — ✓ couvert (reçu de {over} MAD en plus)', { total: two(tot), over: two(a - tot) });
  }
  return t('Prix de la commande : {total} MAD — ✓ même montant que le reçu', { total: two(tot) });
}

/**
 * What « Valider » / « Refuser » sends.
 *
 * ⚠️ THE TYPE TRAVELS ONLY WHEN A PERSON PICKED IT. Left out, the server
 * compares the amount with the order's price AT DECISION TIME (an album added
 * since the slip was read changes the answer); the pre-filled type is a
 * display of that same comparison, never an override of it.
 */
export function decisionBody(
  decision: 'accept' | 'reject',
  amount: string,
  kind: 'advance' | 'full',
  kindChosen: boolean,
): { decision: 'accept' | 'reject'; amount?: number; kind?: 'advance' | 'full' } {
  if (decision !== 'accept' || !(Number(amount) > 0)) return { decision };
  return { decision, amount: Number(amount), ...(kindChosen ? { kind } : {}) };
}

function Row({ p }: { p: PaymentProposal }) {
  const { t } = useT();
  const qc = useQueryClient();

  // ⚠️ PRE-FILLED, NOT LOCKED. Correcting the reading is the normal case — a
  // person is in this loop precisely because a vision model reading a phone
  // photo of a Moroccan bank slip gets the decimal point wrong.
  const [amount, setAmount] = useState<string>(p.amount != null ? String(p.amount) : '');
  // ⚠️ THE TYPE FOLLOWS THE PRICE UNTIL A PERSON PICKS ONE (2026-09-29). The
  // server makes the same comparison at decision time when no type is sent.
  const [chosenKind, setChosenKind] = useState<'advance' | 'full' | null>(null);
  const kind: 'advance' | 'full' = chosenKind
    ?? kindFromPrice(Number(amount), p.order?.total_price)
    ?? (p.kind === 'full' ? 'full' : 'advance');
  const setKind = (k: 'advance' | 'full') => setChosenKind(k);
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (decision: 'accept' | 'reject') =>
      ordersApi.decidePaymentProposal(p.id, decisionBody(decision, amount, kind, chosenKind != null)),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['payment-proposals'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const total = Number(p.order?.total_price);
  const mismatch = depositMismatch(kind, Number(amount), p.order?.total_price);
  // The amount typed (what will be recorded) against the order's price.
  const priceLine = priceComparisonLine(Number(amount) > 0 ? Number(amount) : p.amount, p.order?.total_price, t);

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-gray-900 truncate">
            {p.order?.shopify_order_name ?? t('Commande inconnue')}
          </p>
          <p className="text-[11.5px] text-gray-500 truncate">
            {p.order?.customer_name ?? t('Sans nom')}
            {Number.isFinite(total) && total > 0 ? ` · ${total} MAD ${t('au total')}` : ''}
          </p>
        </div>
        {/* ⚠️ NOT COLOUR ALONE. The badge carries its meaning in words; the
            colour only reinforces it. */}
        <span
          className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            p.verdict_ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
          }`}
        >
          {p.verdict_ok ? t('lecture cohérente') : t('à vérifier')}
        </span>
      </div>

      {/* The three things the owner asked to be checked, side by side. */}
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px]">
        <dt className="text-gray-500">{t('Montant lu')}</dt>
        <dd className="text-gray-800">
          {p.amount != null ? `${p.amount} MAD` : t('illisible')}
        </dd>
        <dt className="text-gray-500">{t('Bénéficiaire')}</dt>
        <dd className="text-gray-800">{orUnreadable(p.payee, t)}</dd>
        <dt className="text-gray-500">{t('Date du reçu')}</dt>
        <dd className="text-gray-800">{orUnreadable(p.paid_on, t)}</dd>
      </dl>

      {/* The receipt against what the order costs — one line (owner, 2026-09-29). */}
      {priceLine && (
        <p
          className={`mt-2 text-[12px] font-medium ${priceLine.includes('⚠️') ? 'text-amber-900' : 'text-emerald-800'}`}
          role="status"
        >
          {priceLine}
        </p>
      )}

      {!p.verdict_ok && p.verdict_reason && (
        <p className="mt-2 text-[11.5px] text-amber-900 bg-amber-50 rounded-lg px-2.5 py-1.5">
          {t('Contrôle automatique')} : {p.verdict_reason}
        </p>
      )}

      {p.proof_url && (
        <a
          href={p.proof_url} target="_blank" rel="noopener noreferrer"
          className="mt-2 inline-block text-[12px] font-semibold text-amber-700 underline underline-offset-2"
        >
          {t('Ouvrir le reçu')} ↗
        </a>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          {/* A visible label, not a placeholder — the field holds money. */}
          <label
            htmlFor={`amt-${p.id}`}
            className="block text-[11px] font-semibold text-gray-600 mb-1"
          >
            {t('Acompte client (MAD)')}
          </label>
          <input
            id={`amt-${p.id}`}
            type="number" inputMode="decimal" min="0" step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-32 h-11 rounded-lg border border-gray-300 px-3 text-[14px]
                       focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>

        <fieldset className="min-w-0">
          <legend className="text-[11px] font-semibold text-gray-600 mb-1">
            {t('Type de paiement')}
          </legend>
          <div className="flex gap-1.5">
            {(['advance', 'full'] as const).map((k) => (
              <button
                key={k} type="button" onClick={() => setKind(k)}
                aria-pressed={kind === k}
                className={`h-11 px-3 rounded-lg text-[12.5px] font-semibold border ${
                  kind === k
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                {k === 'advance' ? t('Acompte') : t('Paiement complet')}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      {/* ⚠️ STATED, NEVER AUTO-CORRECTED. 300 MAD filed as "paiement complet"
          on an 821 MAD album is how the other 521 never arrives. */}
      {mismatch && (
        <p className="mt-2 text-[11.5px] text-amber-900" role="status">
          {kind === 'full'
            ? t('Ce montant ne couvre pas le total — est-ce bien un paiement complet ?')
            : t('Ce montant couvre le total — est-ce bien un acompte ?')}
        </p>
      )}

      {error && (
        <p className="mt-2 text-[11.5px] text-red-600" role="alert">{error}</p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => decide.mutate('accept')}
          disabled={!canAccept(amount, decide.isPending)}
          className="h-11 px-4 rounded-lg text-[13px] font-semibold bg-emerald-600 text-white
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {decide.isPending ? t('Enregistrement…') : t('Valider le paiement')}
        </button>
        <button
          type="button"
          onClick={() => decide.mutate('reject')}
          disabled={decide.isPending}
          className="h-11 px-4 rounded-lg text-[13px] font-semibold border border-gray-300
                     text-gray-700 disabled:opacity-40"
        >
          {t('Refuser')}
        </button>
      </div>

      {/* The sentence that keeps the two decisions apart. */}
      <p className="mt-2 text-[11.5px] text-gray-500">
        {t('Valider enregistre l’argent reçu. La commande reste à confirmer à la main.')}
      </p>
    </div>
  );
}

export function PaymentProposalsCard() {
  const { t } = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['payment-proposals'],
    queryFn: () => ordersApi.paymentProposals(),
    // Money waiting on a decision should not need a page reload to appear.
    refetchInterval: 60_000,
  });

  const proposals = data?.proposals ?? [];

  return (
    <SettingGroup
      title={t('Reçus à valider')}
      summary={
        isLoading
          ? t('Chargement…')
          : proposals.length === 0
            ? t('Rien en attente')
            : `${proposals.length} ${t('reçu(s) en attente de votre décision')}`
      }
      // ⚠️ OPENS ITSELF WHEN MONEY IS WAITING. A proposal behind a closed
      // accordion is the pile-up this screen exists to prevent.
      tone={proposals.length > 0 ? 'sensitive' : 'default'}
      defaultOpen={proposals.length > 0}
      loading={isLoading}
    >
      <p className="text-[11.5px] text-gray-500 mt-3">
        {t(
          'L’assistant lit les reçus mais n’écrit RIEN sur la commande. Vérifiez le montant, '
          + 'le bénéficiaire et la date, corrigez si besoin, puis validez. Le montant validé va '
          + 'dans « Acompte client ».',
        )}
      </p>

      {isError && (
        <p className="text-[11.5px] text-red-600 mt-3" role="alert">
          {t('Impossible de charger les reçus en attente.')}
        </p>
      )}

      {!isLoading && !isError && proposals.length === 0 && (
        <p className="text-[12px] text-gray-400 mt-3">
          {t('Aucun reçu n’attend de décision.')}
        </p>
      )}

      <div className="mt-3 space-y-2">
        {proposals.map((p) => <Row key={p.id} p={p} />)}
      </div>
    </SettingGroup>
  );
}
